import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface ClientNotification {
  id: string;
  client_id: string;
  title: string;
  message: string;
  category: string;
  type: string;
  priority: string;
  is_read: boolean;
  read_at: string | null;
  action_url: string | null;
  action_label: string | null;
  metadata: any;
  created_at: string;
  updated_at: string;
}

export const useClientNotifications = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [notifications, setNotifications] = useState<ClientNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadNotifications = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      // Get client IDs: direct ownership OR delegate access
      const [{ data: clientRows }, { data: delegateRows }] = await Promise.all([
        supabase
          .from('clients')
          .select('id')
          .eq('user_id', user.id)
          .eq('is_active', true),
        supabase
          .from('client_delegates')
          .select('client_id')
          .or(`user_id.eq.${user.id},email.eq.${user.email}`)
          .eq('is_active', true),
      ]);

      const clientIds = [
        ...(clientRows || []).map(c => c.id),
        ...(delegateRows || []).map(d => d.client_id),
      ].filter((id, i, arr) => arr.indexOf(id) === i);

      if (clientIds.length === 0) {
        setLoading(false);
        return;
      }

      // Load notifications
      const { data, error } = await supabase
        .from('client_notifications')
        .select('*')
        .in('client_id', clientIds)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setNotifications(data || []);
      setUnreadCount((data || []).filter(n => !n.is_read).length);
    } catch (error: any) {
      console.error('Error loading notifications:', error);
      toast({
        title: "Error loading notifications",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();

    // Subscribe to real-time updates
    const channel = supabase
      .channel('client-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'client_notifications'
        },
        () => {
          loadNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('client_notifications')
        .update({ 
          is_read: true, 
          read_at: new Date().toISOString() 
        })
        .eq('id', notificationId);

      if (error) throw error;

      // Update local state
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error: any) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user?.id) return;

    try {
      const [{ data: clientRows }, { data: delegateRows }] = await Promise.all([
        supabase.from('clients').select('id').eq('user_id', user.id).eq('is_active', true),
        supabase.from('client_delegates').select('client_id')
          .or(`user_id.eq.${user.id},email.eq.${user.email}`)
          .eq('is_active', true),
      ]);

      const clientIds = [
        ...(clientRows || []).map(c => c.id),
        ...(delegateRows || []).map(d => d.client_id),
      ].filter((id, i, arr) => arr.indexOf(id) === i);

      if (clientIds.length === 0) return;

      const { error } = await supabase
        .from('client_notifications')
        .update({ 
          is_read: true, 
          read_at: new Date().toISOString() 
        })
        .in('client_id', clientIds)
        .eq('is_read', false);

      if (error) throw error;

      loadNotifications();
    } catch (error: any) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    const previous = notifications;
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    setUnreadCount(prev => {
      const removed = previous.find(n => n.id === notificationId);
      return removed && !removed.is_read ? Math.max(0, prev - 1) : prev;
    });

    try {
      const { error } = await supabase
        .from('client_notifications')
        .delete()
        .eq('id', notificationId);
      if (error) throw error;
    } catch (error: any) {
      console.error('Error deleting notification:', error);
      setNotifications(previous);
      setUnreadCount(previous.filter(n => !n.is_read).length);
      toast({
        title: 'Could not clear notification',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const clearAll = async () => {
    if (!user?.id) return;
    const previous = notifications;
    setNotifications([]);
    setUnreadCount(0);

    try {
      const [{ data: clientRows }, { data: delegateRows }] = await Promise.all([
        supabase.from('clients').select('id').eq('user_id', user.id).eq('is_active', true),
        supabase.from('client_delegates').select('client_id')
          .or(`user_id.eq.${user.id},email.eq.${user.email}`)
          .eq('is_active', true),
      ]);

      const clientIds = [
        ...(clientRows || []).map(c => c.id),
        ...(delegateRows || []).map(d => d.client_id),
      ].filter((id, i, arr) => arr.indexOf(id) === i);

      if (clientIds.length === 0) return;

      const { error } = await supabase
        .from('client_notifications')
        .delete()
        .in('client_id', clientIds);
      if (error) throw error;
    } catch (error: any) {
      console.error('Error clearing notifications:', error);
      setNotifications(previous);
      setUnreadCount(previous.filter(n => !n.is_read).length);
      toast({
        title: 'Could not clear notifications',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  return {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    reload: loadNotifications
  };
};
